import { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PhotoLightbox, PhotoThumb } from '@/components/ui/PhotoLightbox';
import { adminService, type AdminAttendanceStatus } from '@/services/admin';
import { MonthlyAttendanceCalendar } from '../../components/admin/reports/MonthlyAttendanceCalendar';
import YearlyAttendanceGrid from '../../components/admin/reports/YearlyAttendanceGrid';
import type { ClassSummary } from '@/types';
import { AttendanceOutOfBlock, OfficeAttendanceHeading, getAttendanceDenominator, getAttendancePercentage, getOfficeAttendanceDenominator, getOfficeAttendanceHeading, getOfficeAttendanceMissed, getOfficeMarksCaption, isAttendanceSingleDay, toLocalISODate } from './attendanceMetricUtils';
import { AttendanceWhoWhenBar, AttendanceAnswerFade, currentMonthToTodayRange, deriveAttendanceWhenChip, type AttendanceWhenChip, type AttendanceWhoScope } from './AttendanceWhoWhenBar';
import { startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, format, isWeekend, isBefore, isWithinInterval } from 'date-fns';
import { resolvePersonPhoto } from '@/utils/avatars';

type Period = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom' | 'range';
type Scope = 'school' | 'class';

type StudentAttendanceSummary = {
  id: string;
  name: string;
  className: string;
  summary: {
    present: number;
    absent: number;
    late: number;
    leave: number;
    total: number;
    missed: number;
  };
  dailyRecords: Array<{ date: string; status: string }>;
  photo?: string | null;
  image?: string | null;
  fatherName?: string;
  sectionName?: string;
  classId?: string;
};

type StudentsData = {
  students: StudentAttendanceSummary[];
  startDate: string;
  endDate: string;
  days_count?: number;
};

const attendanceStatusOptions: Array<{ label: string; value: AdminAttendanceStatus; className: string }> = [
  { label: 'Present', value: 'PRESENT', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  { label: 'Absent', value: 'ABSENT', className: 'border-rose-200 bg-rose-50 text-rose-700' },
  { label: 'Late', value: 'LATE', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  { label: 'Leave', value: 'LEAVE', className: 'border-blue-200 bg-blue-50 text-blue-700' },
];

const normalizeAttendanceStatus = (status?: string | null): AdminAttendanceStatus | null => {
  if (!status) return null;
  const normalized = status.toUpperCase();
  return ['PRESENT', 'ABSENT', 'LATE', 'LEAVE'].includes(normalized)
    ? normalized as AdminAttendanceStatus
    : null;
};

const getStudentRecordForDate = (student: StudentAttendanceSummary, date: string) =>
  student.dailyRecords?.find((record) => record.date === date);

const getStudentDraftStatus = (student: StudentAttendanceSummary, date: string): AdminAttendanceStatus =>
  normalizeAttendanceStatus(getStudentRecordForDate(student, date)?.status) ?? 'PRESENT';

const STUDENT_CLASS_SECTION_FALLBACK = '__class_scope__';

// Sub-component for Student Detail Modal Content
const StudentDetailContent = ({
  student,
  availableYears,
  viewMode = 'monthly',
  initialDate = new Date(),
  highlightDate
}: {
  student: { id: string; name: string; records: Array<{ date: string; status: string }>; photo?: string | null; image?: string | null; fatherName?: string; sectionName?: string; className?: string };
  availableYears: { min: number; max: number };
  viewMode?: 'monthly' | 'yearly';
  initialDate?: Date;
  highlightDate?: Date;
}) => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(initialDate);

  // Monthly Data Fetch
  const { data: historyData, isLoading } = useQuery({
    queryKey: ['student-attendance-history', student.id, format(currentDate, 'yyyy-MM')],
    queryFn: () => adminService.getStudentAttendanceHistory(student.id, format(currentDate, 'yyyy-MM')),
    enabled: !!student.id && viewMode === 'monthly'
  });

  // Yearly Data Fetch
  const { data: yearlyData, isLoading: isLoadingYearly } = useQuery({
    queryKey: ['student-attendance-yearly', student.id, currentDate.getFullYear(), 'v2'], // v2 to force refresh
    queryFn: () => adminService.getStudentYearlyHistory(student.id, currentDate.getFullYear()),
    enabled: !!student.id && viewMode === 'yearly'
  });

  const currentMonthStart = startOfMonth(currentDate);
  const currentMonthEnd = endOfMonth(currentDate);
  const calendarDays = eachDayOfInterval({ start: currentMonthStart, end: currentMonthEnd });

  const records = historyData?.records || [];
  const holidays = historyData?.holidays || [];
  const summary = historyData?.summary || { present: 0, absent: 0, late: 0, leave: 0, missed: 0, expected: 0 };

  const yearlySummary = yearlyData?.summary || { present: 0, absent: 0, late: 0, leave: 0, recorded_records: 0, expected_records: 0 };
  const yearlyMonths = yearlyData?.months || [];
  const monthlyExpected = getAttendanceDenominator(summary);
  const yearlyExpected = getAttendanceDenominator(yearlySummary);

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = parseInt(e.target.value);
    const newDate = new Date(currentDate.getFullYear(), newMonth - 1, 1);
    setCurrentDate(newDate);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(e.target.value);
    const newDate = new Date(newYear, currentDate.getMonth(), 1);
    setCurrentDate(newDate);
  };

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div className="h-16 w-16 rounded-full overflow-hidden bg-white border-2 border-white shadow-sm flex-shrink-0 ring-2 ring-slate-200">
          <img src={resolvePersonPhoto(student, 'student')} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-slate-800">{student.name}</h3>
          <div className="text-sm text-slate-500 space-y-1">
            {student.fatherName && <div>Father: <span className="font-medium text-slate-700">{student.fatherName}</span></div>}
            <div className="flex gap-4">
              {student.className && <div>Class: <span className="font-medium text-slate-700">{student.className}</span></div>}
              {student.sectionName && <div>Section: <span className="font-medium text-slate-700">{student.sectionName}</span></div>}
            </div>
          </div>
        </div>
        <Button variant="secondary" onClick={() => navigate(`/admin/reports/students/${student.id}`)}>
          View report
        </Button>
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
        <div className="text-sm font-medium text-slate-600">Viewing {viewMode === 'yearly' ? 'Yearly' : 'Monthly'} History for:</div>
        <div className="flex gap-2">
          {viewMode === 'monthly' && (
            <select
              value={currentDate.getMonth() + 1}
              onChange={handleMonthChange}
              className="rounded-md border-slate-300 text-sm focus:border-brand focus:ring-brand"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          )}
          <select
            value={currentDate.getFullYear()}
            onChange={handleYearChange}
            className="rounded-md border-slate-300 text-sm focus:border-brand focus:ring-brand"
          >
            {Array.from(
              { length: availableYears.max - availableYears.min + 1 },
              (_, i) => availableYears.max - i
            ).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {(viewMode === 'monthly' ? isLoading : isLoadingYearly) ? (
        <div className="h-48 flex items-center justify-center text-slate-400">Loading history...</div>
      ) : (
        <>
          {viewMode === 'monthly' ? (
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left Col: Overview */}
              <div className="md:w-1/3 space-y-6">
                <Card>
                  <div className="text-center p-4">
                    <div className="w-20 h-20 bg-slate-200 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl">
                      🎓
                    </div>
                    <h2 className="text-xl font-bold text-slate-800">{student.name}</h2>
                    <p className="text-sm text-slate-500">Attendance Profile</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <AttendanceOutOfBlock label="Present" count={summary.present} total={monthlyExpected} tone="emerald" display="percent" />
                    <AttendanceOutOfBlock label="Absent" count={summary.absent} total={monthlyExpected} tone="rose" display="percent" />
                    <AttendanceOutOfBlock label="Late" count={summary.late} total={monthlyExpected} tone="amber" display="percent" />
                    <AttendanceOutOfBlock label="Leave" count={summary.leave} total={monthlyExpected} tone="blue" display="percent" />
                    <AttendanceOutOfBlock label="Unmarked" count={summary.missed || 0} total={monthlyExpected} tone="slate" className="col-span-2 md:col-span-1" display="percent" />
                  </div>

                  {/* Stacked Progress Bar */}
                  <div className="mt-4 px-1">
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden flex">
                      {summary.present > 0 && (
                        <div className="h-full bg-emerald-500" style={{ width: `${(summary.present / (summary.expected || 1)) * 100}%` }} title={`Present: ${summary.present}`} />
                      )}
                      {summary.absent > 0 && (
                        <div className="h-full bg-rose-500" style={{ width: `${(summary.absent / (summary.expected || 1)) * 100}%` }} title={`Absent: ${summary.absent}`} />
                      )}
                      {summary.late > 0 && (
                        <div className="h-full bg-amber-500" style={{ width: `${(summary.late / (summary.expected || 1)) * 100}%` }} title={`Late: ${summary.late}`} />
                      )}
                      {summary.leave > 0 && (
                        <div className="h-full bg-blue-500" style={{ width: `${(summary.leave / (summary.expected || 1)) * 100}%` }} title={`Leave: ${summary.leave}`} />
                      )}
                      {(summary.missed || 0) > 0 && (
                        <div className="h-full bg-slate-400" style={{ width: `${((summary.missed || 0) / (summary.expected || 1)) * 100}%` }} title={`Unmarked: ${summary.missed || 0}`} />
                      )}
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-400 mt-1 px-1">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Right Col: Calendar */}
              <div className="md:w-2/3">
                <Card title={`Calendar: ${format(currentDate, 'MMMM yyyy')}`}>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="text-center text-xs font-semibold text-slate-400 py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: currentMonthStart.getDay() }).map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square bg-transparent" />
                    ))}
                    {calendarDays.map((day) => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      // Check Holiday
                      const holiday = holidays.find((h: any) =>
                        isWithinInterval(day, { start: parseISO(h.start), end: parseISO(h.end) })
                      );

                      const record = records.find((r: any) => r.date === dateStr);
                      let status = (record?.status || 'none').toLowerCase();

                      // Use configured off-days from summary
                      const offDays = summary.offDays || [0, 6];
                      const isOffDay = offDays.includes(day.getDay());

                      if (holiday || isOffDay) {
                        status = 'holiday';
                      }

                      // Unmarked Logic: Must NOT be an off-day, MUST be in the past, and NO record
                      const isUnmarked = !isOffDay && !holiday && isBefore(day, new Date()) && !isSameDay(day, new Date()) && status === 'none';

                      let bgClass = 'bg-slate-50';
                      let textClass = 'text-slate-400';
                      let ringClass = '';

                      if (status === 'present') {
                        bgClass = 'bg-emerald-100';
                        textClass = 'text-emerald-700 font-bold';
                      } else if (status === 'absent') {
                        bgClass = 'bg-rose-100';
                        textClass = 'text-rose-700 font-bold';
                      } else if (status === 'late') {
                        bgClass = 'bg-amber-100';
                        textClass = 'text-amber-700 font-bold';
                      } else if (status === 'leave') {
                        bgClass = 'bg-blue-100';
                        textClass = 'text-blue-700 font-bold';
                      } else if (status === 'holiday') {
                        bgClass = 'bg-purple-100';
                        textClass = 'text-purple-700 font-bold';
                      } else if (isUnmarked) {
                        bgClass = 'bg-slate-200';
                        textClass = 'text-slate-500 font-medium italic';
                      }

                      if (isSameDay(day, new Date())) {
                        ringClass = 'ring-2 ring-brand ring-offset-1';
                      }

                      if (highlightDate && isSameDay(day, highlightDate)) {
                        ringClass = 'ring-2 ring-blue-500 ring-offset-2 z-10 relative shadow-lg';
                      }

                      let label = '';
                      if (status === 'present') label = 'P';
                      else if (status === 'absent') label = 'A';
                      else if (status === 'late') label = 'L';
                      else if (status === 'leave') label = 'Lv';
                      else if (status === 'holiday') label = 'Off';
                      else if (isUnmarked) label = '—';

                      return (
                        <div
                          key={dateStr}
                          className={`aspect-square rounded-lg flex flex-col items-center justify-center p-0.5 text-sm ${bgClass} ${textClass} ${ringClass} group relative overflow-hidden`}
                          title={holiday ? holiday.name : (isWeekend(day) ? 'Weekend' : undefined)}
                        >
                          <span className="leading-none font-medium">{format(day, 'd')}</span>
                          {label ? (
                            <span className="text-[10px] font-bold leading-none truncate overflow-hidden w-full text-center mt-0.5">
                              {label}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Yearly Overview Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <AttendanceOutOfBlock label="Present" count={yearlySummary.present} total={yearlyExpected} tone="emerald" className="p-3.5 shadow-sm min-w-0" display="percent" />
                <AttendanceOutOfBlock label="Absent" count={yearlySummary.absent} total={yearlyExpected} tone="rose" className="p-3.5 shadow-sm min-w-0" display="percent" />
                <AttendanceOutOfBlock label="Late" count={yearlySummary.late} total={yearlyExpected} tone="amber" className="p-3.5 shadow-sm min-w-0" display="percent" />
                <AttendanceOutOfBlock label="On Leave" count={yearlySummary.leave} total={yearlyExpected} tone="blue" className="p-3.5 shadow-sm min-w-0" display="percent" />
                <AttendanceOutOfBlock label="Unmarked" count={yearlySummary.missed || 0} total={yearlyExpected} tone="slate" className="p-3.5 shadow-sm min-w-0" display="percent" />
                <Card className="text-center flex flex-col items-center justify-center p-3.5 min-w-0">
                  <div className="text-2xl font-bold tabular-nums text-slate-700 leading-tight">
                    {getAttendancePercentage(yearlySummary.present, yearlyExpected)}%
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase leading-tight text-slate-500">
                    Rate
                  </div>
                </Card>
              </div>

              {/* Stacked Progress Bar (Yearly) */}
              <div className="mb-6 px-1">
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden flex">
                  {yearlySummary.present > 0 && (
                    <div className="h-full bg-emerald-500" style={{ width: `${(yearlySummary.present / (yearlySummary.expected_records || 1)) * 100}%` }} title={`Present: ${yearlySummary.present}`} />
                  )}
                  {yearlySummary.absent > 0 && (
                    <div className="h-full bg-rose-500" style={{ width: `${(yearlySummary.absent / (yearlySummary.expected_records || 1)) * 100}%` }} title={`Absent: ${yearlySummary.absent}`} />
                  )}
                  {yearlySummary.late > 0 && (
                    <div className="h-full bg-amber-500" style={{ width: `${(yearlySummary.late / (yearlySummary.expected_records || 1)) * 100}%` }} title={`Late: ${yearlySummary.late}`} />
                  )}
                  {yearlySummary.leave > 0 && (
                    <div className="h-full bg-blue-500" style={{ width: `${(yearlySummary.leave / (yearlySummary.expected_records || 1)) * 100}%` }} title={`Leave: ${yearlySummary.leave}`} />
                  )}
                  {(yearlySummary.missed || 0) > 0 && (
                    <div className="h-full bg-slate-400" style={{ width: `${((yearlySummary.missed || 0) / (yearlySummary.expected_records || 1)) * 100}%` }} title={`Unmarked: ${yearlySummary.missed || 0}`} />
                  )}
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1 px-1">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Yearly Grid */}
              <YearlyAttendanceGrid
                data={yearlyMonths}
                isLoading={isLoadingYearly}
              // No click handler needed for student detail view unless we want to drill down to monthly
              />
            </div>
          )
          }
        </>
      )}
    </div >
  );
};


export default function AdminAttendancePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL if available
  const urlClassId = searchParams.get('class');
  const urlMonth = searchParams.get('month');
  const urlYear = searchParams.get('year');

  const [scope, setScope] = useState<Scope>(urlClassId ? 'class' : 'school');
  const [selectedClass, setSelectedClass] = useState<string>(urlClassId || '');
  const [selectedGrade, setSelectedGrade] = useState<string>('');
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [selectedTeacher, setSelectedTeacher] = useState<string>('');
  // Ref to prevent circular dependency between Section→Teacher and Teacher→Grade effects
  const isSystemTeacherUpdate = useRef(false);
  const [timeFilterMode, setTimeFilterMode] = useState<'period' | 'day'>('period');

  const [period, setPeriod] = useState<Period>('range');

  // New State for Dynamic Period Logic
  const [periodSubType, setPeriodSubType] = useState<'monthly' | 'yearly' | 'custom'>('monthly');
  const [availableYears, setAvailableYears] = useState<{ min: number; max: number }>({ min: new Date().getFullYear(), max: new Date().getFullYear() });
  const [selectedYear, setSelectedYear] = useState<number>(urlYear ? Number(urlYear) : new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(urlMonth ? Number(urlMonth) : new Date().getMonth() + 1);
  const [customDate, setCustomDate] = useState<string>(toLocalISODate(new Date()));
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(currentMonthToTodayRange());

  // Derived State for Calendar Context
  const getCustomYearMonth = () => {
    if (!customDate) return { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };
    const [y, m] = customDate.split('-').map(Number);
    return { year: y, month: m };
  };

  const currentYear = timeFilterMode === 'day' ? getCustomYearMonth().year : selectedYear;
  const currentMonth = timeFilterMode === 'day' ? getCustomYearMonth().month : selectedMonth;

  // Sorting State
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Sorting Function
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Helper component for sort icon
  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    if (sortConfig?.key !== columnKey) return <span className="ml-1 text-slate-300">↕</span>;
    return sortConfig.direction === 'asc' ? <span className="ml-1 text-brand">↑</span> : <span className="ml-1 text-brand">↓</span>;
  };

  // Fetch Available Years
  const { data: yearsData } = useQuery({
    queryKey: ['available-years'],
    queryFn: adminService.getAvailableYears,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (yearsData) {
      setAvailableYears(yearsData);
    }
  }, [yearsData]);

  // Effect: Update Date Range based on Period SubType Selection
  useEffect(() => {
    if (timeFilterMode === 'period') {
      if (periodSubType === 'monthly') {
        const start = new Date(selectedYear, selectedMonth - 1, 1);
        const end = new Date(selectedYear, selectedMonth, 0); // Last day of month
        // Adjust for timezone offset to avoid -1 day errors on ISO string
        const toLocalISO = (date: Date) => {
          const offset = date.getTimezoneOffset() * 60000;
          return new Date(date.getTime() - offset).toISOString().split('T')[0];
        };

        setPeriod('range');
        setDateRange({ start: toLocalISO(start), end: toLocalISO(end) });
      } else if (periodSubType === 'yearly') {
        setPeriod('range');
        setDateRange({
          start: `${selectedYear}-01-01`,
          end: `${selectedYear}-12-31`
        });
      } else if (periodSubType === 'custom') {
        setPeriod('range');
        // Keep existing dateRange
      }
    }
  }, [timeFilterMode, periodSubType, selectedYear, selectedMonth]);

  const [searchQuery, setSearchQuery] = useState(''); // New search state
  const [selectedStudent, setSelectedStudent] = useState<{ id: string; name: string; records: Array<{ date: string; status: string }>; photo?: string | null; image?: string | null; fatherName?: string; sectionName?: string; className?: string } | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [isTakeAttendanceMode, setIsTakeAttendanceMode] = useState(false);
  const [studentAttendanceSectionChoice, setStudentAttendanceSectionChoice] = useState('');
  const [studentAttendanceDraft, setStudentAttendanceDraft] = useState<Record<string, AdminAttendanceStatus>>({});
  const [studentAttendanceTouched, setStudentAttendanceTouched] = useState<Set<string>>(new Set());
  const [studentAttendanceToast, setStudentAttendanceToast] = useState<string | null>(null);

  const saveStudentAttendanceMutation = useMutation({
    mutationFn: adminService.saveStudentAttendanceBatch,
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-school-attendance-summary-v2'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-attendance-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-students-daily-attendance'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-monthly-overview'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-yearly-overview'] }),
        queryClient.invalidateQueries({ queryKey: ['student-attendance-history'] }),
        queryClient.invalidateQueries({ queryKey: ['student-attendance-yearly'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-overview'] }),
      ]);
      setStudentAttendanceToast(`Student attendance saved for ${result.date}.`);
      setStudentAttendanceTouched(new Set());
    },
  });

  useEffect(() => {
    if (!studentAttendanceToast) return undefined;
    const timer = window.setTimeout(() => setStudentAttendanceToast(null), 3500);
    return () => window.clearTimeout(timer);
  }, [studentAttendanceToast]);

  const { data: classes = [] } = useQuery<ClassSummary[]>({
    queryKey: ['admin-classes'],
    queryFn: adminService.listClasses,
  });

  const { data: sections = [] } = useQuery({
    queryKey: ['admin-sections'],
    queryFn: adminService.listSections,
  });

  const { data: teachers = [] } = useQuery({
    queryKey: ['admin-teachers'],
    queryFn: adminService.listTeachers,
  });

  const availableSectionsForGrade = useMemo(() => {
    const sourceClasses = selectedGrade
      ? classes.filter(c => c.gradeLevel === selectedGrade)
      : classes;
    const sectionMap = new Map<string, string>();

    sourceClasses.forEach((classItem) => {
      if (!classItem.sectionId) {
        return;
      }
      sectionMap.set(
        classItem.sectionId,
        sections.find(s => s.id === classItem.sectionId)?.name || 'Unknown'
      );
    });

    if (!selectedGrade && sectionMap.size === 0) {
      sections.forEach((section) => sectionMap.set(section.id, section.name || 'Unknown'));
    }

    return Array.from(sectionMap, ([id, name]) => ({ id, name }));
  }, [selectedGrade, classes, sections]);

  const selectedClassOption = useMemo(
    () => classes.find((classItem) => classItem.id === selectedClass),
    [classes, selectedClass]
  );

  const studentAttendanceSectionOptions = useMemo(() => {
    if (selectedClassOption?.sectionId) {
      return [{
        id: selectedClassOption.sectionId,
        name: sections.find((section) => section.id === selectedClassOption.sectionId)?.name || 'Unknown',
      }];
    }

    if (selectedClassOption) {
      return [{
        id: STUDENT_CLASS_SECTION_FALLBACK,
        name: 'Default Section',
      }];
    }

    return availableSectionsForGrade;
  }, [availableSectionsForGrade, sections, selectedClassOption]);

  const isStudentAttendanceScopeReady = isTakeAttendanceMode && !!selectedClass && !!studentAttendanceSectionChoice;

  const hasClassificationFilter = !!selectedClass || !!selectedGrade || !!selectedSection || !!selectedTeacher;

  const selectedClassIdSet = useMemo(() => {
    if (selectedClass) {
      return new Set([selectedClass]);
    }

    if (!hasClassificationFilter) {
      return new Set<string>();
    }

    return new Set(
      classes
        .filter((classItem) => {
          const gradeMatches = !selectedGrade || classItem.gradeLevel === selectedGrade;
          const sectionMatches = !selectedSection || classItem.sectionId === selectedSection;
          const teacherMatches = !selectedTeacher || classItem.teacherId === selectedTeacher;
          return gradeMatches && sectionMatches && teacherMatches;
        })
        .map((classItem) => classItem.id)
    );
  }, [classes, hasClassificationFilter, selectedClass, selectedGrade, selectedSection, selectedTeacher]);

  useEffect(() => {
    if (selectedSection && !availableSectionsForGrade.some((section) => section.id === selectedSection)) {
      setSelectedSection('');
      setSelectedClass('');
      setSelectedTeacher('');
    }
  }, [availableSectionsForGrade, selectedSection]);

  const activeFilterLabel = useMemo(() => {
    const labels: string[] = [];
    if (selectedGrade) {
      labels.push(selectedGrade);
    }
    if (selectedSection) {
      labels.push(availableSectionsForGrade.find((section) => section.id === selectedSection)?.name || 'Section');
    }
    if (selectedTeacher) {
      const teacher = teachers.find((item: any) => item.id === selectedTeacher);
      if (teacher) {
        labels.push(`${teacher.firstName} ${teacher.lastName}`);
      }
    }

    return labels.length ? labels.join(' / ') : null;
  }, [availableSectionsForGrade, selectedGrade, selectedSection, selectedTeacher, teachers]);

  // Set default class if switch to class mode and none selected
  useEffect(() => {
    // Only default if we haven't even picked a grade yet
    if (!isTakeAttendanceMode && scope === 'class' && !selectedClass && !selectedGrade && classes.length > 0) {
      setSelectedClass(classes[0].id);
    }
  }, [scope, classes, selectedClass, selectedGrade, isTakeAttendanceMode]);

  // Handle Teacher -> Class/Section/Grade selection (only for USER-initiated teacher changes)
  useEffect(() => {
    // Skip if this teacher change was triggered by the Section->Teacher effect
    if (isSystemTeacherUpdate.current) {
      isSystemTeacherUpdate.current = false;
      return;
    }
    if (selectedTeacher) {
      const assignedClass = classes.find((c: any) => c.teacherId === selectedTeacher);
      if (assignedClass) {
        if (assignedClass.gradeLevel !== selectedGrade) {
          setSelectedGrade(assignedClass.gradeLevel);
        }
        if (assignedClass.id !== selectedClass) {
          setSelectedClass(assignedClass.id);
        }
        if (assignedClass.sectionId !== selectedSection) {
          setSelectedSection(assignedClass.sectionId || '');
        }
      }
    }
  }, [selectedTeacher, classes]);

  // Handle Section -> Teacher selection (within a class context)
  useEffect(() => {
    if (selectedGrade && selectedSection) {
      const match = classes.find((c: any) => c.gradeLevel === selectedGrade && c.sectionId === selectedSection);
      if (match && match.teacherId && match.teacherId !== selectedTeacher) {
        // Mark as system-initiated so Teacher->Grade effect is skipped
        isSystemTeacherUpdate.current = true;
        setSelectedTeacher(match.teacherId);
      }
    }
  }, [selectedGrade, selectedSection, classes]);

  // Unified Student Attendance Data
  const { data: studentsData, isLoading } = useQuery<StudentsData>({
    queryKey: ['admin-students-daily-attendance', scope, selectedClass, selectedSection, selectedTeacher, selectedGrade, period, customDate, dateRange],
    queryFn: () => {
      const isCustom = period === 'custom';
      const isRange = period === 'range';
      return adminService.getStudentDailyAttendance({
        classId: selectedClass || undefined,
        gradeLevel: !selectedClass ? selectedGrade || undefined : undefined,
        sectionId: selectedSection || undefined,
        teacherId: selectedTeacher || undefined,
        period: (isCustom || isRange) ? 'daily' : period as any,
        startDate: isCustom ? customDate : (isRange ? dateRange.start : undefined),
        endDate: isCustom ? customDate : (isRange ? dateRange.end : undefined),
      })
    },
    enabled: scope === 'school' || scope === 'class',
  });

  const criteriaFilteredStudents = useMemo(() => {
    if (!studentsData) {
      return [];
    }

    if (!hasClassificationFilter) {
      return studentsData.students;
    }

    if (selectedClassIdSet.size === 0) {
      return [];
    }

    return studentsData.students.filter((student) => student.classId && selectedClassIdSet.has(student.classId));
  }, [studentsData, hasClassificationFilter, selectedClassIdSet]);

  const aggregatedStudentSummary = useMemo(() => {
    if (!studentsData) {
      return null;
    }

    const totals = criteriaFilteredStudents.reduce(
      (acc, student) => {
        acc.present += student.summary.present || 0;
        acc.absent += student.summary.absent || 0;
        acc.late += student.summary.late || 0;
        acc.leave += student.summary.leave || 0;
        acc.missed += student.summary.missed || 0;
        acc.totalRecords += student.summary.total || 0;
        return acc;
      },
      { present: 0, absent: 0, late: 0, leave: 0, missed: 0, totalRecords: 0 }
    );

    return {
      period,
      startDate: studentsData.startDate,
      endDate: studentsData.endDate,
      summary: {
        total: criteriaFilteredStudents.length,
        total_records: totals.totalRecords,
        expected_records: totals.totalRecords + totals.missed,
        missed: totals.missed,
        present: totals.present,
        absent: totals.absent,
        late: totals.late,
        leave: totals.leave,
        messageSent: 0,
        school_days: studentsData.days_count || 0,
      },
      students: [],
      records: [],
    };
  }, [criteriaFilteredStudents, period, studentsData]);

  const getPercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
  };

  // Class-specific attendance summary
  const { data: classSummary } = useQuery({
    queryKey: ['admin-attendance-summary', selectedClass, period, customDate, dateRange],
    queryFn: () => {
      const isCustom = period === 'custom';
      const isRange = period === 'range';
      return adminService.getAttendanceSummary(selectedClass, {
        period: (isCustom || isRange) ? 'daily' : period as any,
        startDate: isCustom ? customDate : (isRange ? dateRange.start : undefined),
        endDate: isCustom ? customDate : (isRange ? dateRange.end : undefined)
      })
    },
    enabled: scope === 'class' && !!selectedClass,
  });

  // School-wide attendance summary
  const { data: schoolSummary } = useQuery({
    queryKey: ['admin-school-attendance-summary-v2', period, customDate, dateRange, selectedSection, selectedTeacher],
    queryFn: () => {
      const isCustom = period === 'custom';
      const isRange = period === 'range';
      return adminService.getSchoolAttendanceSummary({
        period: period as any,
        sectionId: selectedSection || undefined,
        teacherId: selectedTeacher || undefined,
        startDate: isCustom ? customDate : (isRange ? dateRange.start : undefined),
        endDate: isCustom ? customDate : (isRange ? dateRange.end : undefined)
      })
    },
    enabled: scope === 'school',
  });

  const shouldUseAggregatedSummary = hasClassificationFilter && (!selectedClass || scope === 'school');
  const summary = selectedClass
    ? classSummary
    : shouldUseAggregatedSummary
      ? aggregatedStudentSummary
      : schoolSummary;
  const attendanceSingleDay = isAttendanceSingleDay({
    timeFilterMode,
    startDate: summary?.startDate,
    endDate: summary?.endDate,
  });
  const officeHeading = getOfficeAttendanceHeading({
    singleDay: attendanceSingleDay,
    periodSubType,
    selectedYear,
    selectedMonth,
    customDate,
    startDate: summary?.startDate ?? dateRange.start,
    endDate: summary?.endDate ?? dateRange.end,
  });
  const summaryExpected = summary ? getOfficeAttendanceDenominator(summary.summary, attendanceSingleDay) : 0;
  const summaryMissed = summary ? getOfficeAttendanceMissed(summary.summary, attendanceSingleDay) : 0;
  const summaryBarDenom = summaryExpected || 1;
  const summaryMarksCaption = summary
    ? getOfficeMarksCaption(summary.summary, {
        singleDay: attendanceSingleDay,
        peopleWord: 'students',
        unfinished: officeHeading.unfinished,
      })
    : '';
  const summaryCardDisplay = attendanceSingleDay ? 'people' : 'percent';
  const whenChip = deriveAttendanceWhenChip({ timeFilterMode, periodSubType });

  const monthlyOverview = useQuery({
    queryKey: ['admin-monthly-overview', currentYear, currentMonth, scope, selectedClass, selectedGrade, selectedSection, selectedTeacher],
    queryFn: () => adminService.getMonthlyAttendanceOverview({
      year: currentYear,
      month: currentMonth,
      classId: selectedClass || undefined,
      gradeLevel: !selectedClass ? selectedGrade || undefined : undefined,
      sectionId: selectedSection || undefined,
      teacherId: selectedTeacher || undefined,
    }),
    enabled: periodSubType === 'monthly' || periodSubType === 'custom' || timeFilterMode === 'day'
  });

  const yearlyOverview = useQuery({
    queryKey: ['admin-yearly-overview', selectedYear, scope, selectedClass, selectedGrade, selectedSection, selectedTeacher],
    queryFn: () => adminService.getStudentYearlyAttendanceOverview({
      year: selectedYear,
      classId: selectedClass || undefined,
      gradeLevel: !selectedClass ? selectedGrade || undefined : undefined,
      sectionId: selectedSection || undefined,
      teacherId: selectedTeacher || undefined,
    }),
    enabled: periodSubType === 'yearly'
  });

  // 3. Filter and Sort Students
  const filteredAndSortedStudents = useMemo(() => {
    if (!studentsData) return [];

    // Filter
    let result = criteriaFilteredStudents.filter(student =>
      student.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort
    if (sortConfig !== null) {
      result.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'name':
            aValue = a.name;
            bValue = b.name;
            break;
          case 'className':
            aValue = a.className;
            bValue = b.className;
            break;
          case 'present':
            aValue = a.summary.present;
            bValue = b.summary.present;
            break;
          case 'absent':
            aValue = a.summary.absent;
            bValue = b.summary.absent;
            break;
          case 'late':
            aValue = a.summary.late;
            bValue = b.summary.late;
            break;
          case 'leave':
            aValue = a.summary.leave;
            bValue = b.summary.leave;
            break;
          case 'missed':
            aValue = a.summary.missed || 0;
            bValue = b.summary.missed || 0;
            break;
          case 'total':
            aValue = a.summary.total;
            bValue = b.summary.total;
            break;
          case 'percentage':
            aValue = parseFloat(getPercentage(a.summary.present, a.summary.total + (a.summary.missed || 0)));
            bValue = parseFloat(getPercentage(b.summary.present, b.summary.total + (b.summary.missed || 0)));
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [criteriaFilteredStudents, studentsData, searchQuery, sortConfig]);

  useEffect(() => {
    if (!isTakeAttendanceMode || timeFilterMode !== 'day') {
      return;
    }

    setStudentAttendanceDraft((current) => {
      const next: Record<string, AdminAttendanceStatus> = {};
      criteriaFilteredStudents.forEach((student) => {
        next[student.id] = current[student.id] ?? getStudentDraftStatus(student, customDate);
      });
      return next;
    });
  }, [criteriaFilteredStudents, customDate, isTakeAttendanceMode, timeFilterMode]);

  const startStudentAttendanceMode = () => {
    setCustomDate(toLocalISODate(new Date()));
    setScope('class');
    setSelectedGrade('');
    setSelectedClass('');
    setSelectedSection('');
    setSelectedTeacher('');
    setSearchQuery('');
    setStudentAttendanceSectionChoice('');
    setStudentAttendanceDraft({});
    setStudentAttendanceTouched(new Set());
    setTimeFilterMode('day');
    setPeriod('custom');
    setIsTakeAttendanceMode(true);
    setStudentAttendanceToast(null);
    window.setTimeout(() => {
      document.getElementById('attendance-mark-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const cancelStudentAttendanceMode = () => {
    setIsTakeAttendanceMode(false);
    setStudentAttendanceSectionChoice('');
    setStudentAttendanceDraft({});
    setStudentAttendanceTouched(new Set());
  };

  const handleStudentAttendanceClassChange = (classId: string) => {
    const classItem = classes.find((item) => item.id === classId);
    setSelectedClass(classId);
    setSelectedSection('');
    setSelectedTeacher('');
    setStudentAttendanceSectionChoice('');
    setStudentAttendanceDraft({});
    setStudentAttendanceTouched(new Set());

    if (classItem) {
      setSelectedGrade(classItem.gradeLevel);
    } else {
      setSelectedGrade('');
    }
  };

  const handleStudentAttendanceSectionChange = (sectionId: string) => {
    setStudentAttendanceSectionChoice(sectionId);
    setSelectedSection(sectionId === STUDENT_CLASS_SECTION_FALLBACK ? '' : sectionId);
    setStudentAttendanceDraft({});
    setStudentAttendanceTouched(new Set());
  };

  const setStudentDraftStatus = (studentId: string, status: AdminAttendanceStatus) => {
    setStudentAttendanceDraft((current) => ({ ...current, [studentId]: status }));
    setStudentAttendanceTouched((current) => {
      const next = new Set(current);
      next.add(studentId);
      return next;
    });
  };

  const markAllStudentDrafts = (status: AdminAttendanceStatus) => {
    setStudentAttendanceDraft(
      criteriaFilteredStudents.reduce<Record<string, AdminAttendanceStatus>>((acc, student) => {
        acc[student.id] = status;
        return acc;
      }, {})
    );
    setStudentAttendanceTouched(new Set(criteriaFilteredStudents.map((student) => student.id)));
  };

  const saveStudentAttendance = () => {
    const records = criteriaFilteredStudents
      .filter((student) => studentAttendanceTouched.has(student.id))
      .map((student) => ({
        id: student.id,
        status: studentAttendanceDraft[student.id] ?? getStudentDraftStatus(student, customDate),
      }));

    if (!records.length) {
      return;
    }

    saveStudentAttendanceMutation.mutate({
      date: customDate,
      records,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present': return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      case 'absent': return 'bg-rose-50 border-rose-200 text-rose-700';
      case 'late': return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'leave': return 'bg-blue-50 border-blue-200 text-blue-700';
      default: return 'bg-slate-50 border-slate-200 text-slate-700';
    }
  };

  const applyWhoScope = (next: AttendanceWhoScope) => {
    if (next === 'school') {
      setScope('school');
      setSelectedClass('');
      setSelectedGrade('');
      setSelectedSection('');
      setSelectedTeacher('');
      return;
    }
    setScope('class');
  };

  const applyWhoClass = (classId: string) => {
    const classItem = classes.find((item) => item.id === classId);
    setSelectedClass(classId);
    setSelectedGrade(classItem?.gradeLevel || '');
    setSelectedSection(classItem?.sectionId || '');
    setSelectedTeacher('');
  };

  const applyWhenChip = (chip: AttendanceWhenChip) => {
    const now = new Date();
    if (chip !== 'today') {
      setIsTakeAttendanceMode(false);
      setStudentAttendanceSectionChoice('');
      setStudentAttendanceDraft({});
      setStudentAttendanceTouched(new Set());
    }
    if (chip === 'today') {
      setTimeFilterMode('day');
      setPeriod('custom');
      setCustomDate(toLocalISODate(now));
      return;
    }
    setTimeFilterMode('period');
    if (chip === 'month') {
      setPeriodSubType('monthly');
      setSelectedMonth(now.getMonth() + 1);
      setSelectedYear(now.getFullYear());
      return;
    }
    if (chip === 'year') {
      setPeriodSubType('yearly');
      setSelectedYear(now.getFullYear());
      return;
    }
  };

  const handleViewStudent = (student: StudentAttendanceSummary) => {
    setSelectedStudent({
      id: student.id,
      name: student.name,
      records: student.dailyRecords || [],
      photo: student.photo,
      image: student.image,
      fatherName: student.fatherName,
      sectionName: student.sectionName,
      className: student.className
    });
  };



  return (
    <DashboardShell title="Student Attendance" role="admin">
      <div className="space-y-6">
        {studentAttendanceToast && (
          <div className="fixed bottom-6 right-6 z-50 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 shadow-lg">
            {studentAttendanceToast}
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Student Attendance</h1>
            <p className="text-sm text-slate-500">Who, when, and what happened.</p>
          </div>
          <Button variant="primary" onClick={startStudentAttendanceMode}>
            Take Attendance
          </Button>
        </div>

        <AttendanceWhoWhenBar
          scope={scope}
          onScopeChange={applyWhoScope}
          classId={selectedClass}
          onClassChange={applyWhoClass}
          classes={classes}
          whenChip={whenChip}
          onWhenChip={applyWhenChip}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          availableYears={availableYears}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />

        {/* Summary Cards */}
        {summary && (
          <AttendanceAnswerFade motionKey={`${officeHeading.title}:${summaryCardDisplay}:${scope}:${selectedClass}`}>
          <div className="mb-6">
            <OfficeAttendanceHeading
              title={officeHeading.title}
              caption={attendanceSingleDay ? undefined : summaryMarksCaption}
            />
            <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
              <Card title="Total Students">
                <p className="text-3xl font-bold text-slate-900">{summary.summary.total}</p>
                <p className="text-sm text-slate-500 mt-1">
                  Active
                </p>
              </Card>

              <AttendanceOutOfBlock label="Present" count={summary.summary.present} total={summaryExpected} tone="emerald" className="p-6 shadow-sm" display={summaryCardDisplay} />
              <AttendanceOutOfBlock label="Absent" count={summary.summary.absent} total={summaryExpected} tone="rose" className="p-6 shadow-sm" display={summaryCardDisplay} />
              <AttendanceOutOfBlock label="Late" count={summary.summary.late} total={summaryExpected} tone="amber" className="p-6 shadow-sm" display={summaryCardDisplay} />
              <AttendanceOutOfBlock label="On Leave" count={summary.summary.leave} total={summaryExpected} tone="blue" className="p-6 shadow-sm" display={summaryCardDisplay} />
              <AttendanceOutOfBlock label="Unmarked" count={summaryMissed} total={summaryExpected} tone="slate" className="p-6 shadow-sm" display={summaryCardDisplay} />
            </div>
          </div>
          </AttendanceAnswerFade>
        )}

        {/* Stacked Progress Bar (School Overview) */}
        {summary && (
          <div className="mb-6 px-1">
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden flex">
              {summary.summary.present > 0 && (
                <div className="h-full bg-emerald-500" style={{ width: `${(summary.summary.present / summaryBarDenom) * 100}%` }} title={`Present: ${summary.summary.present}`} />
              )}
              {summary.summary.absent > 0 && (
                <div className="h-full bg-rose-500" style={{ width: `${(summary.summary.absent / summaryBarDenom) * 100}%` }} title={`Absent: ${summary.summary.absent}`} />
              )}
              {summary.summary.late > 0 && (
                <div className="h-full bg-amber-500" style={{ width: `${(summary.summary.late / summaryBarDenom) * 100}%` }} title={`Late: ${summary.summary.late}`} />
              )}
              {summary.summary.leave > 0 && (
                <div className="h-full bg-blue-500" style={{ width: `${(summary.summary.leave / summaryBarDenom) * 100}%` }} title={`Leave: ${summary.summary.leave}`} />
              )}
              {summaryMissed > 0 && (
                <div className="h-full bg-slate-400" style={{ width: `${(summaryMissed / summaryBarDenom) * 100}%` }} title={`Unmarked: ${summaryMissed}`} />
              )}
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-1 px-1">
              <span>0%</span>
              <span>50%</span>
              <span>{attendanceSingleDay ? `100% (${summaryMarksCaption})` : '100%'}</span>
            </div>
          </div>
        )}

        {/* Monthly Attendance Calendar */}
        {((timeFilterMode === 'period' && (periodSubType === 'monthly' || periodSubType === 'custom')) || timeFilterMode === 'day') && (
          <div className="mb-6">
            <MonthlyAttendanceCalendar
              data={monthlyOverview.data || []}
              year={currentYear}
              month={currentMonth}
              isLoading={monthlyOverview.isLoading}
              highlightDate={timeFilterMode === 'day' ? customDate : undefined}
              onDayClick={(dateStr) => {
                setTimeFilterMode('day');
                setPeriod('custom');
                setCustomDate(dateStr);
              }}
            />
          </div>
        )}

        {/* Yearly Attendance Grid */}
        {timeFilterMode === 'period' && periodSubType === 'yearly' && (
          <div className="mb-6">
            <YearlyAttendanceGrid
              data={Array.isArray(yearlyOverview.data?.months) ? yearlyOverview.data.months : []}
              isLoading={yearlyOverview.isLoading}
              onMonthClick={(monthNum) => {
                setPeriodSubType('monthly');
                setSelectedMonth(monthNum);
                // Ensure we stay in period mode or switch to it if needed (though already in period mode here)
                setTimeFilterMode('period');
              }}
            />
          </div>
        )}

        {/* Student List View */}
        {studentsData && (
          <div id="attendance-mark-list">
          <Card title={isTakeAttendanceMode
            ? isStudentAttendanceScopeReady
              ? `Take Attendance (${criteriaFilteredStudents.length} students)`
              : 'Take Attendance'
            : `Attendance Records (${criteriaFilteredStudents.length} students)`
          }>
            <div className="space-y-4">
              {isTakeAttendanceMode && (
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Choose Attendance Scope</p>
                      <p className="mt-1 text-sm text-slate-500">Please select a Class and Section to begin taking attendance.</p>
                    </div>
                    <Button variant="secondary" onClick={cancelStudentAttendanceMode}>
                      Cancel
                    </Button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Class</label>
                      <select
                        aria-label="Attendance class"
                        value={selectedClass}
                        onChange={(e) => handleStudentAttendanceClassChange(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition-colors focus:border-brand focus:ring-2 focus:ring-brand/30"
                      >
                        <option value="">Select class</option>
                        {classes.map((classItem) => (
                          <option key={classItem.id} value={classItem.id}>
                            {classItem.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Section</label>
                      <select
                        aria-label="Attendance section"
                        value={studentAttendanceSectionChoice}
                        onChange={(e) => handleStudentAttendanceSectionChange(e.target.value)}
                        disabled={!selectedClass}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition-colors focus:border-brand focus:ring-2 focus:ring-brand/30 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                      >
                        <option value="">{selectedClass ? 'Select section' : 'Select a class first'}</option>
                        {studentAttendanceSectionOptions.map((section) => (
                          <option key={section.id} value={section.id}>
                            {section.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {isStudentAttendanceScopeReady && (
                    <div className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                      Ready for {selectedClassOption?.name || 'selected class'} / {studentAttendanceSectionOptions.find((section) => section.id === studentAttendanceSectionChoice)?.name || 'selected section'} on {customDate}.
                    </div>
                  )}
                </div>
              )}

              {(!isTakeAttendanceMode || isStudentAttendanceScopeReady) && (
              <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                <div className="flex-1">
                  <strong>{activeFilterLabel || (scope === 'school' ? 'Whole School' : 'Class')} View:</strong> {studentsData.startDate} to {studentsData.endDate}
                </div>

                {/* Search Bar */}
                <div className="flex-1 w-full md:w-auto relative">
                  <input
                    type="text"
                    placeholder="Search student name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-md border-slate-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm pl-9"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                <div className="flex-none text-slate-500 italic text-xs">
                  {isTakeAttendanceMode ? `Taking attendance for ${customDate}` : period === 'daily' ? 'Showing present day status' : 'Showing aggregated stats'}
                </div>
              </div>
              )}

              {isTakeAttendanceMode && isStudentAttendanceScopeReady && (
                <div className="flex flex-col gap-3 rounded-lg border border-brand/20 bg-brand/5 p-4 text-sm text-slate-700 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="font-semibold text-slate-900">Take Attendance</p>
                    <p className="text-slate-500">Unmarked students show Present in this list. Save writes only the students you marked.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="ghost" onClick={() => markAllStudentDrafts('PRESENT')} disabled={!criteriaFilteredStudents.length}>
                      Mark All Present
                    </Button>
                    <Button variant="ghost" onClick={() => markAllStudentDrafts('ABSENT')} disabled={!criteriaFilteredStudents.length}>
                      Mark All Absent
                    </Button>
                    <Button variant="secondary" onClick={cancelStudentAttendanceMode}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={saveStudentAttendance}
                      disabled={!studentAttendanceTouched.size || saveStudentAttendanceMutation.isPending}
                    >
                      {saveStudentAttendanceMutation.isPending ? 'Saving...' : 'Save Attendance'}
                    </Button>
                  </div>
                </div>
              )}

              {isTakeAttendanceMode ? (
                isStudentAttendanceScopeReady ? (
                  <>
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Name
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-12">
                              Photo
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Class
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 bg-white">
                          {filteredAndSortedStudents.map((student) => {
                            const selectedStatus = studentAttendanceDraft[student.id] ?? getStudentDraftStatus(student, customDate);
                            return (
                              <tr key={student.id} className="hover:bg-slate-50">
                                <td className="px-3 py-3 text-sm font-medium text-slate-900">{student.name}</td>
                                <td className="px-3 py-3">
                                  <PhotoThumb
                                    src={resolvePersonPhoto(student, 'student')}
                                    sizeClass="h-9 w-9"
                                    onClick={() => setPreviewSrc(resolvePersonPhoto(student, 'student'))}
                                  />
                                </td>
                                <td className="px-3 py-3 text-sm text-slate-600">{student.className}</td>
                                <td className="px-3 py-3">
                                  <div className="flex flex-wrap gap-2">
                                    {attendanceStatusOptions.map((option) => (
                                      <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => setStudentDraftStatus(student.id, option.value)}
                                        className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${selectedStatus === option.value
                                          ? option.className
                                          : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                                          }`}
                                      >
                                        {option.label}
                                      </button>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {filteredAndSortedStudents.length === 0 && (
                            <tr>
                              <td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-500">
                                No students match these filters. Change class or date, or take attendance for this day.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile V4 Sticky Take Header + Status Pill Cards */}
                    <div className="md:hidden space-y-3">
                      {/* V4 Sticky Header */}
                      <div className="bg-slate-900 text-white rounded-2xl p-3 shadow-md sticky top-2 z-20 space-y-2 border border-slate-800">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-[10px] font-black text-sky-400 uppercase tracking-wider">Attendance Take-Mode</div>
                            <div className="text-xs font-bold text-white">
                              {selectedClassOption?.name || 'Class'} • {criteriaFilteredStudents.length} Students
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => markAllStudentDrafts('PRESENT')}
                              disabled={!criteriaFilteredStudents.length}
                              className="text-[10px] font-extrabold bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white px-2.5 py-1 rounded-lg shadow-sm cursor-pointer"
                            >
                              ⚡ All P
                            </button>
                            <button
                              type="button"
                              onClick={saveStudentAttendance}
                              disabled={!studentAttendanceTouched.size || saveStudentAttendanceMutation.isPending}
                              className="text-[10px] font-extrabold bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white px-2.5 py-1 rounded-lg shadow-sm disabled:opacity-50 cursor-pointer"
                            >
                              {saveStudentAttendanceMutation.isPending ? '...' : 'Save'}
                            </button>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="space-y-1">
                          <div className="flex justify-between text-[10px] text-slate-300 font-semibold">
                            <span>Class Progress</span>
                            <span>
                              {studentAttendanceTouched.size} / {criteriaFilteredStudents.length} Marked (
                              {criteriaFilteredStudents.length > 0 ? Math.round((studentAttendanceTouched.size / criteriaFilteredStudents.length) * 100) : 0}%)
                            </span>
                          </div>
                          <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden flex shadow-inner">
                            <div
                              className="bg-emerald-500 h-full transition-all"
                              style={{
                                width: `${criteriaFilteredStudents.length > 0 ? (criteriaFilteredStudents.filter(s => (studentAttendanceDraft[s.id] ?? getStudentDraftStatus(s, customDate)) === 'PRESENT').length / criteriaFilteredStudents.length) * 100 : 0}%`
                              }}
                            />
                            <div
                              className="bg-rose-500 h-full transition-all"
                              style={{
                                width: `${criteriaFilteredStudents.length > 0 ? (criteriaFilteredStudents.filter(s => (studentAttendanceDraft[s.id] ?? getStudentDraftStatus(s, customDate)) === 'ABSENT').length / criteriaFilteredStudents.length) * 100 : 0}%`
                              }}
                            />
                            <div
                              className="bg-amber-500 h-full transition-all"
                              style={{
                                width: `${criteriaFilteredStudents.length > 0 ? (criteriaFilteredStudents.filter(s => (studentAttendanceDraft[s.id] ?? getStudentDraftStatus(s, customDate)) === 'LATE').length / criteriaFilteredStudents.length) * 100 : 0}%`
                              }}
                            />
                            <div
                              className="bg-blue-500 h-full transition-all"
                              style={{
                                width: `${criteriaFilteredStudents.length > 0 ? (criteriaFilteredStudents.filter(s => (studentAttendanceDraft[s.id] ?? getStudentDraftStatus(s, customDate)) === 'LEAVE').length / criteriaFilteredStudents.length) * 100 : 0}%`
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Mobile Student List with Status Pills */}
                      <div className="space-y-2">
                        {filteredAndSortedStudents.map((student) => {
                          const selectedStatus = studentAttendanceDraft[student.id] ?? getStudentDraftStatus(student, customDate);
                          const isTouched = studentAttendanceTouched.has(student.id);

                          return (
                            <div key={student.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-2.5 transition-all">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2.5">
                                  <PhotoThumb
                                    src={resolvePersonPhoto(student, 'student')}
                                    sizeClass="h-9 w-9"
                                    onClick={() => setPreviewSrc(resolvePersonPhoto(student, 'student'))}
                                  />
                                  <div>
                                    <div className="text-xs font-bold text-slate-900">{student.name}</div>
                                    <div className="text-[10px] text-slate-400">
                                      {student.className} {student.fatherName ? `• ${student.fatherName}` : ''}
                                    </div>
                                  </div>
                                </div>
                                {isTouched && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    Marked
                                  </span>
                                )}
                              </div>

                              {/* V4 Status Pills: ~44px Touch Targets */}
                              <div className="grid grid-cols-4 gap-1.5 pt-0.5">
                                <button
                                  type="button"
                                  onClick={() => setStudentDraftStatus(student.id, 'PRESENT')}
                                  className={`h-11 rounded-xl text-xs font-bold transition-all border flex flex-col justify-center items-center cursor-pointer active:scale-95 ${
                                    selectedStatus === 'PRESENT'
                                      ? 'bg-emerald-600 text-white border-emerald-600 shadow ring-2 ring-emerald-300'
                                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                  }`}
                                >
                                  <span className="text-[11px] leading-none">✓</span>
                                  <span className="text-[10px] leading-tight">Pres</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setStudentDraftStatus(student.id, 'ABSENT')}
                                  className={`h-11 rounded-xl text-xs font-bold transition-all border flex flex-col justify-center items-center cursor-pointer active:scale-95 ${
                                    selectedStatus === 'ABSENT'
                                      ? 'bg-rose-600 text-white border-rose-600 shadow ring-2 ring-rose-300'
                                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                  }`}
                                >
                                  <span className="text-[11px] leading-none">✕</span>
                                  <span className="text-[10px] leading-tight">Abs</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setStudentDraftStatus(student.id, 'LATE')}
                                  className={`h-11 rounded-xl text-xs font-bold transition-all border flex flex-col justify-center items-center cursor-pointer active:scale-95 ${
                                    selectedStatus === 'LATE'
                                      ? 'bg-amber-500 text-white border-amber-500 shadow ring-2 ring-amber-300'
                                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                  }`}
                                >
                                  <span className="text-[11px] leading-none">⏱</span>
                                  <span className="text-[10px] leading-tight">Late</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setStudentDraftStatus(student.id, 'LEAVE')}
                                  className={`h-11 rounded-xl text-xs font-bold transition-all border flex flex-col justify-center items-center cursor-pointer active:scale-95 ${
                                    selectedStatus === 'LEAVE'
                                      ? 'bg-blue-600 text-white border-blue-600 shadow ring-2 ring-blue-300'
                                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                  }`}
                                >
                                  <span className="text-[11px] leading-none">✈</span>
                                  <span className="text-[10px] leading-tight">Leave</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {filteredAndSortedStudents.length === 0 && (
                          <div className="p-6 text-center text-sm text-slate-500 bg-slate-50 rounded-xl border">
                            No students match these filters.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
                    <p className="text-sm font-semibold text-slate-800">Please select a Class and Section to begin taking attendance.</p>
                    <p className="mt-1 text-sm text-slate-500">The student list and save controls will appear after the scope is complete.</p>
                  </div>
                )
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          <th
                            className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                            onClick={() => handleSort('name')}
                          >
                            Student Name <SortIcon columnKey="name" />
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-12">
                            Photo
                          </th>
                          <th
                            className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                            onClick={() => handleSort('className')}
                          >
                            Class <SortIcon columnKey="className" />
                          </th>
                          {timeFilterMode !== 'day' && (
                            <th
                              className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                              onClick={() => handleSort('present')}
                            >
                              Present <SortIcon columnKey="present" />
                            </th>
                          )}
                          {timeFilterMode !== 'day' && (
                            <th
                              className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                              onClick={() => handleSort('absent')}
                            >
                              Absent <SortIcon columnKey="absent" />
                            </th>
                          )}
                          {timeFilterMode !== 'day' && (
                            <th
                              className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                              onClick={() => handleSort('late')}
                            >
                              Late <SortIcon columnKey="late" />
                            </th>
                          )}
                          {timeFilterMode !== 'day' && (
                            <th
                              className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                              onClick={() => handleSort('leave')}
                            >
                              Leave <SortIcon columnKey="leave" />
                            </th>
                          )}
                          {timeFilterMode !== 'day' && (
                            <th
                              className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                              onClick={() => handleSort('missed')}
                            >
                              Unmarked <SortIcon columnKey="missed" />
                            </th>
                          )}
                          {timeFilterMode !== 'day' && (
                            <th
                              className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                              onClick={() => handleSort('total')}
                            >
                              Days Recorded <SortIcon columnKey="total" />
                            </th>
                          )}
                          <th
                            className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none min-w-[140px]"
                            onClick={() => handleSort('percentage')}
                          >
                            {timeFilterMode === 'day' ? 'Status' : 'Attendance %'} <SortIcon columnKey="percentage" />
                          </th>
                          <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {filteredAndSortedStudents.map((student: StudentAttendanceSummary) => {
                          const expected = student.summary.total + (student.summary.missed || 0);
                          const attendanceRate = getPercentage(student.summary.present, expected);
                          return (
                            <tr key={student.id} className="hover:bg-slate-50">
                              <td className="px-3 py-3 text-sm font-medium text-slate-900">
                                {student.name}
                              </td>
                              <td className="px-3 py-3">
                                <PhotoThumb
                                  src={resolvePersonPhoto(student, 'student')}
                                  sizeClass="h-8 w-8"
                                  onClick={() => setPreviewSrc(resolvePersonPhoto(student, 'student'))}
                                />
                              </td>
                              <td className="px-3 py-3 text-sm text-slate-600">
                                {student.className}
                              </td>
                              {timeFilterMode !== 'day' && (
                                <td className="px-3 py-3 text-sm text-center">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                    {student.summary.present}
                                  </span>
                                </td>
                              )}
                              {timeFilterMode !== 'day' && (
                                <td className="px-3 py-3 text-sm text-center">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-800">
                                    {student.summary.absent}
                                  </span>
                                </td>
                              )}
                              {timeFilterMode !== 'day' && (
                                <td className="px-3 py-3 text-sm text-center">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    {student.summary.late}
                                  </span>
                                </td>
                              )}
                              {timeFilterMode !== 'day' && (
                                <td className="px-3 py-3 text-sm text-center">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {student.summary.leave}
                                  </span>
                                </td>
                              )}
                              {timeFilterMode !== 'day' && (
                                <td className="px-3 py-3 text-sm text-center">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500">
                                    {student.summary.missed || 0}
                                  </span>
                                </td>
                              )}
                              {timeFilterMode !== 'day' && (
                                <td className="px-3 py-3 text-sm text-center font-semibold text-slate-700">
                                  {student.summary.total}
                                </td>
                              )}
                              <td className="px-3 py-3 text-sm text-center min-w-[140px]">
                                {timeFilterMode === 'day' ? (
                                  <div className="flex justify-center">
                                    {student.summary.present > 0 ? (
                                      <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                        PRESENT
                                      </span>
                                    ) : student.summary.absent > 0 ? (
                                      <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-bold bg-rose-100 text-rose-800 border border-rose-200">
                                        ABSENT
                                      </span>
                                    ) : student.summary.late > 0 ? (
                                      <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                        LATE
                                      </span>
                                    ) : student.summary.leave > 0 ? (
                                      <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                        LEAVE
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                                        UNMARKED
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center gap-1.5">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${parseFloat(attendanceRate) >= 90 ? 'bg-emerald-100 text-emerald-800' :
                                      parseFloat(attendanceRate) >= 75 ? 'bg-amber-100 text-amber-800' :
                                        'bg-rose-100 text-rose-800'
                                      }`}>
                                      {attendanceRate}%
                                    </span>

                                    <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden flex max-w-[120px]">
                                      {student.summary.present > 0 && <div className="h-full bg-emerald-500" style={{ width: `${(student.summary.present / (student.summary.total + (student.summary.missed || 0))) * 100}%` }} title="Present" />}
                                      {student.summary.absent > 0 && <div className="h-full bg-rose-500" style={{ width: `${(student.summary.absent / (student.summary.total + (student.summary.missed || 0))) * 100}%` }} title="Absent" />}
                                      {student.summary.late > 0 && <div className="h-full bg-amber-500" style={{ width: `${(student.summary.late / (student.summary.total + (student.summary.missed || 0))) * 100}%` }} title="Late" />}
                                      {student.summary.leave > 0 && <div className="h-full bg-blue-500" style={{ width: `${(student.summary.leave / (student.summary.total + (student.summary.missed || 0))) * 100}%` }} title="Leave" />}
                                      {(student.summary.missed || 0) > 0 && <div className="h-full bg-slate-400" style={{ width: `${((student.summary.missed || 0) / (student.summary.total + (student.summary.missed || 0))) * 100}%` }} title="Unmarked" />}
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-3 text-sm text-center">
                                <Button
                                  variant="secondary"
                                  onClick={() => handleViewStudent(student)}
                                  className="text-xs"
                                >
                                  View Details
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                        {filteredAndSortedStudents.length === 0 && (
                          <tr>
                            <td colSpan={9} className="px-3 py-6 text-center text-sm text-slate-500">
                              {scope === 'class' && !selectedClass && !hasClassificationFilter
                                ? 'Select a class to view students'
                                : 'No students match these filters. Change class or date, or take attendance for this day.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards for Browse Mode */}
                  <div className="md:hidden space-y-2.5">
                    {filteredAndSortedStudents.map((student: StudentAttendanceSummary) => {
                      const expected = student.summary.total + (student.summary.missed || 0);
                      const attendanceRate = getPercentage(student.summary.present, expected);

                      return (
                        <div key={student.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2.5">
                              <PhotoThumb
                                src={resolvePersonPhoto(student, 'student')}
                                sizeClass="h-9 w-9"
                                onClick={() => setPreviewSrc(resolvePersonPhoto(student, 'student'))}
                              />
                              <div>
                                <div className="text-xs font-bold text-slate-900">{student.name}</div>
                                <div className="text-[10px] text-slate-400">{student.className}</div>
                              </div>
                            </div>
                            <Button
                              variant="secondary"
                              onClick={() => handleViewStudent(student)}
                              className="text-xs py-1 px-2.5 h-auto"
                            >
                              Details
                            </Button>
                          </div>

                          {timeFilterMode === 'day' ? (
                            <div className="pt-1.5 flex items-center justify-between border-t border-slate-100 text-xs">
                              <span className="text-slate-500 font-medium">Status</span>
                              {student.summary.present > 0 ? (
                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                  PRESENT
                                </span>
                              ) : student.summary.absent > 0 ? (
                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-rose-100 text-rose-800 border border-rose-200">
                                  ABSENT
                                </span>
                              ) : student.summary.late > 0 ? (
                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                  LATE
                                </span>
                              ) : student.summary.leave > 0 ? (
                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                  LEAVE
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                                  UNMARKED
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-1.5 pt-1.5 border-t border-slate-100">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-[10px] text-slate-400 font-medium">Attendance Rate</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                  parseFloat(attendanceRate) >= 90 ? 'bg-emerald-100 text-emerald-800' :
                                  parseFloat(attendanceRate) >= 75 ? 'bg-amber-100 text-amber-800' :
                                  'bg-rose-100 text-rose-800'
                                }`}>
                                  {attendanceRate}%
                                </span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden flex">
                                {student.summary.present > 0 && <div className="h-full bg-emerald-500" style={{ width: `${(student.summary.present / (expected || 1)) * 100}%` }} />}
                                {student.summary.absent > 0 && <div className="h-full bg-rose-500" style={{ width: `${(student.summary.absent / (expected || 1)) * 100}%` }} />}
                                {student.summary.late > 0 && <div className="h-full bg-amber-500" style={{ width: `${(student.summary.late / (expected || 1)) * 100}%` }} />}
                                {student.summary.leave > 0 && <div className="h-full bg-blue-500" style={{ width: `${(student.summary.leave / (expected || 1)) * 100}%` }} />}
                                {(student.summary.missed || 0) > 0 && <div className="h-full bg-slate-400" style={{ width: `${((student.summary.missed || 0) / (expected || 1)) * 100}%` }} />}
                              </div>
                              <div className="flex justify-between text-[10px] text-slate-500 pt-0.5">
                                <span className="text-emerald-700 font-semibold">P: {student.summary.present}</span>
                                <span className="text-rose-700 font-semibold">A: {student.summary.absent}</span>
                                <span className="text-amber-700 font-semibold">L: {student.summary.late}</span>
                                <span className="text-blue-700 font-semibold">Lv: {student.summary.leave}</span>
                                <span className="text-slate-400">Days: {student.summary.total}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {filteredAndSortedStudents.length === 0 && (
                      <div className="p-6 text-center text-sm text-slate-500 bg-slate-50 rounded-xl border">
                        {scope === 'class' && !selectedClass && !hasClassificationFilter
                          ? 'Select a class to view students'
                          : 'No students match these filters. Change class or date, or take attendance for this day.'}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <Card>
            <div className="py-12 text-center">
              <svg className="animate-spin h-8 w-8 mx-auto text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-2 text-slate-500">Loading attendance data...</p>
            </div>
          </Card>
        )}

        {/* Empty State (Class mode with no class selected) */}
        {scope === 'class' && !selectedClass && !isTakeAttendanceMode && !isLoading && (
          <Card>
            <div className="py-12 text-center">
              <h3 className="mt-2 text-sm font-medium text-slate-900">No class selected</h3>
              <p className="mt-1 text-sm text-slate-500">Choose a class, or switch back to Whole school.</p>
            </div>
          </Card>
        )}

      </div>

      <Modal
        isOpen={!!selectedStudent}
        onClose={() => setSelectedStudent(null)}
        title="Student Attendance Details"
        panelClassName="sm:max-w-5xl"
      >
        {selectedStudent && (
          <StudentDetailContent
            student={selectedStudent}
            availableYears={availableYears}
            viewMode={(timeFilterMode === 'period' && periodSubType === 'yearly') ? 'yearly' : 'monthly'}
            initialDate={(timeFilterMode === 'period' && periodSubType === 'yearly')
              ? new Date(selectedYear, 0, 1)
              : (timeFilterMode === 'period' && periodSubType === 'monthly')
                ? new Date(selectedYear, selectedMonth - 1, 1)
                : (timeFilterMode === 'period' && periodSubType === 'custom')
                  ? new Date(dateRange.end)
                  : new Date(customDate)
            }
            highlightDate={timeFilterMode === 'day' ? new Date(customDate) : undefined}
          />
        )}
      </Modal>
      <PhotoLightbox src={previewSrc} onClose={() => setPreviewSrc(null)} />
    </DashboardShell>
  );
}
